import { motion } from "framer-motion";
import { Bug, Sprout, FlaskConical, Droplets, Wheat, MapPinned, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Bug,
    title: "Pesticide Application",
    desc: "Targeted insecticide spraying for row crops, groves and nurseries — precision passes that protect beneficial insects.",
    img: "https://images.pexels.com/photos/37288742/pexels-photo-37288742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Drone pesticide spraying tomato row crops in Miami-Dade Florida",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    icon: Sprout,
    title: "Herbicide Spraying",
    desc: "Spot-spray or full-field weed control with band-application drones — ideal for groves, fence-lines and ditches.",
    img: "https://images.unsplash.com/photo-1627920769842-6887c6df05ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxhZ3JpY3VsdHVyYWwlMjBkcm9uZSUyMHNwcmF5aW5nfGVufDB8fHx8MTc4MDU3MjgzNnww&ixlib=rb-4.1.0&q=85",
    alt: "Agricultural drone herbicide application Homestead Florida grove",
  },
  {
    icon: FlaskConical,
    title: "Fungicide Treatment",
    desc: "Stop powdery mildew, anthracnose and rust before they spread. Even canopy penetration with downwash technology.",
    img: "https://images.unsplash.com/photo-1713952160156-bb59cac789a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhZ3JpY3VsdHVyYWwlMjBkcm9uZSUyMHNwcmF5aW5nfGVufDB8fHx8MTc4MDU3MjgzNnww&ixlib=rb-4.1.0&q=85",
    alt: "Fungicide drone spraying avocado groves Redland South Florida",
  },
  {
    icon: Droplets,
    title: "Liquid Fertilizer",
    desc: "Foliar feeds and micronutrient top-ups applied with drift-controlled nozzles — boost yield without ground rigs.",
    img: "https://images.unsplash.com/photo-1571091374658-6d880f0eab29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjcm9wJTIwZmllbGQlMjBoYXJ2ZXN0fGVufDB8fHx8MTc4MDU3MjgzNnww&ixlib=rb-4.1.0&q=85",
    alt: "Liquid fertilizer drone application across Florida crop field",
  },
  {
    icon: Wheat,
    title: "Cover Crop Seeding",
    desc: "Aerial broadcast seeding for cover crops and overseeding. Establish residue cover faster than a tractor pass.",
    img: "https://images.unsplash.com/photo-1702896781457-1d4f69aebf7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxjcm9wJTIwZmllbGQlMjBoYXJ2ZXN0fGVufDB8fHx8MTc4MDU3MjgzNnww&ixlib=rb-4.1.0&q=85",
    alt: "Cover crop drone seeding South Florida row crop field",
  },
  {
    icon: MapPinned,
    title: "Crop Scouting & Mapping",
    desc: "Multispectral NDVI mapping and pest scouting flights — turn imagery into prescription spray maps automatically.",
    img: "https://images.unsplash.com/photo-1630873711080-aa3097d1da66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjcm9wJTIwZmllbGQlMjBoYXJ2ZXN0fGVufDB8fHx8MTc4MDU3MjgzNnww&ixlib=rb-4.1.0&q=85",
    alt: "Drone NDVI crop scouting and field mapping Miami-Dade agriculture",
    span: "lg:col-span-2",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-4" data-testid="services-eyebrow">
            What we spray
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary font-bold leading-tight">
            Every chemical, every crop, <span className="text-accent">applied with precision.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
            From large-scale row crops to tropical fruit groves in Homestead & the Redland, our drone fleet handles
            the application your farm needs — safely, accurately and on schedule.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`group relative overflow-hidden rounded-3xl bg-card border border-border shadow-card hover:shadow-card-hover transition-all duration-300 ${s.span || ""}`}
              >
                <div className="relative h-48 lg:h-56 overflow-hidden">
                  <img src={s.img} alt={s.alt || s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
                  <div className="absolute top-4 left-4 h-11 w-11 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center text-primary shadow-card">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-primary group-hover:text-accent transition-colors">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  <a href="#contact" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                    Request this service <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
